import React, { Component, ReactNode } from 'react';

import log from 'loglevel';
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react';

import { TenantError } from '../../types/tenant.types';
import { showErrorDetails } from '../../utils/env';

import './TenantErrorBoundary.css';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class TenantErrorBoundary extends Component<Props, State> {
  declare state: State;
  declare props: Readonly<Props>;
  declare setState: <K extends keyof State>(
    state: ((prevState: Readonly<State>, props: Readonly<Props>) => (Pick<State, K> | State | null)) | Pick<State, K> | State | null,
    callback?: () => void,
  ) => void;

  public constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    log.error('TenantErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Handle tenant-specific errors
    if (error instanceof TenantError) {
      this.handleTenantError(error);
    } else if (
      error.message.includes('tenant') ||
      error.message.includes('Tenant')
    ) {
      this.handleTenantError(new TenantError(error.message, 'TENANT_ERROR'));
    }
  }

  private handleTenantError = (error: TenantError) => {
    log.error('Tenant error occurred:', error);

    // Clear tenant context and redirect based on error type
    if (error.code === 'NO_ACCESS' || error.message.includes('access')) {
      localStorage.removeItem('selectedTenantId');
      setTimeout(() => {
        window.location.href = '/no-access';
      }, 100);
    } else if (
      error.code === 'TENANT_REQUIRED' ||
      error.message.includes('required')
    ) {
      localStorage.removeItem('selectedTenantId');
      setTimeout(() => {
        window.location.href = '/select-tenant';
      }, 100);
    }
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  private handleSelectTenant = () => {
    localStorage.removeItem('selectedTenantId');
    window.location.href = '/select-tenant';
  };

  private handleLogout = () => {
    localStorage.removeItem('selectedTenantId');
    localStorage.removeItem('auth_token');
    window.location.href = '/';
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      const { error } = this.state;
      const isTenantError =
        error instanceof TenantError ||
        error?.message.includes('tenant') ||
        error?.message.includes('Tenant');

      return (
        <div className="tenant-error-boundary">
          <div className="error-container">
            <div className="error-content">
              <AlertTriangle className="error-icon" />

              <h1 className="error-title">
                {isTenantError ? 'Tenant Error' : 'Something went wrong'}
              </h1>

              <p className="error-message">
                {isTenantError
                  ? 'There was an issue with your tenant access. Please try selecting your tenant again.'
                  : 'An unexpected error occurred. Please try again or contact support if the problem persists.'}
              </p>

              {showErrorDetails && error && (
                <details className="error-details">
                  <summary>Error Details</summary>
                  <pre className="error-stack">
                    {error.message}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}

              <div className="error-actions">
                <button
                  className="btn-primary"
                  onClick={this.handleRetry}
                  type="button"
                >
                  <RefreshCw className="btn-icon" />
                  Try Again
                </button>

                {isTenantError && (
                  <button
                    className="btn-secondary"
                    onClick={this.handleSelectTenant}
                    type="button"
                  >
                    Select Tenant
                  </button>
                )}

                <button
                  className="btn-logout"
                  onClick={this.handleLogout}
                  type="button"
                >
                  <LogOut className="btn-icon" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
